import { buildApiEndpoint } from "../hooks/useAppBootstrapFlow";
import { LocalizedMessage } from "../i18n/LocaleContext";
import { FavoriteSource, PabxFavorite, TeamsFavorite, UserFavorites } from "../types/contacts";
import { AuthorizedFetch } from "./tenantAdminApi";

/**
 * Cliente das rotas /api/bearer/userFavorites — os contatos favoritos do usuário.
 *
 * Um verbo por operação: `GET` lê, `POST` marca (a única que insere, logo a única que pode ser
 * recusada por teto ou permissão), `DELETE` desmarca e `PATCH` reconcilia (atualiza a cópia dos
 * contatos que mudaram e remove os que desapareceram da agenda, nunca insere).
 *
 * Nenhuma função envia `tenantId` ou `userId`: o backend os toma do token SSO e o schema recusa
 * identidade no corpo. O teto também não é configurado aqui — vem do servidor em `max`, para que
 * mudá-lo não exija rebuild do bundle.
 */

export type FavoritesPayload = {
  favorites: UserFavorites;
  max: number;
};

/** Falha numa requisição de favoritos, já traduzida para uma mensagem da interface. */
export class FavoritesError extends Error {
  readonly status: number;
  readonly message$: LocalizedMessage;

  constructor(status: number, message$: LocalizedMessage) {
    super(`favorites request failed with status ${status}`);
    this.name = "FavoritesError";
    this.status = status;
    this.message$ = message$;
  }
}

type FavoritesConflictReason = "limit" | "pabxContactsAccess";

const CONFLICT_MESSAGES: Record<FavoritesConflictReason, LocalizedMessage["key"]> = {
  limit: "contactSearch.favorites.error.limit",
  pabxContactsAccess: "contactSearch.favorites.error.noPabxAccess",
};

async function toError(response: Response): Promise<FavoritesError> {
  // 409 (teto) e 403 (permissão do PABX) são os dois casos que a interface explica em vez de
  // mostrar um erro genérico; os dois trazem `reason`, como as rotas de tenant admin.
  if (response.status === 409 || response.status === 403) {
    const body = (await response.json().catch(() => ({}))) as { reason?: FavoritesConflictReason };
    const key = (body.reason && CONFLICT_MESSAGES[body.reason]) || "contactSearch.favorites.error.generic";
    return new FavoritesError(response.status, { key });
  }
  return new FavoritesError(response.status, { key: "contactSearch.favorites.error.generic" });
}

async function request(
  authorizedFetch: AuthorizedFetch,
  method: string,
  body?: unknown,
  signal?: AbortSignal
): Promise<FavoritesPayload> {
  const response = await authorizedFetch(
    buildApiEndpoint("/api/bearer/userFavorites"),
    { method, signal },
    body === undefined ? undefined : JSON.stringify(body)
  );
  if (!response.ok) {
    throw await toError(response);
  }
  return (await response.json()) as FavoritesPayload;
}

export function fetchFavorites(authorizedFetch: AuthorizedFetch, signal?: AbortSignal) {
  return request(authorizedFetch, "GET", undefined, signal);
}

export function addFavorite(
  authorizedFetch: AuthorizedFetch,
  source: FavoriteSource,
  item: PabxFavorite | TeamsFavorite
) {
  return request(authorizedFetch, "POST", { source, item });
}

export function removeFavorite(authorizedFetch: AuthorizedFetch, source: FavoriteSource, id: string) {
  return request(authorizedFetch, "DELETE", { source, id });
}

/**
 * Reconciliação de uma origem. Só é chamada depois de uma carga **completa e sem filtro** da
 * agenda — ver `useFavorites.reconcile`. `removeIds` é destrutivo, então quem decide se a carga
 * autoriza concluir ausência é o chamador, não esta função.
 */
export function syncFavorites(
  authorizedFetch: AuthorizedFetch,
  source: FavoriteSource,
  items: (PabxFavorite | TeamsFavorite)[],
  removeIds: string[]
) {
  return request(authorizedFetch, "PATCH", { source, items, removeIds });
}
